// src/hooks/useLatestResults.js
"use client";

import { useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useGeneralTestLogic } from "../Hooks/DependentTest"; // <- your existing hook
import { CALCULATED_TESTS } from "../Utils/TestNames";
import {
  URINE_PANEL_PARAMETER_SET,
  URINE_PANEL_PARAM_TO_CODE,
} from "../Utils/urineMapping";
import API_BASE_URL from "../config";
import DEFAULT_API_BASE_URL from "../config"

export function useLatestResults(tests, initRef) {
  const token = useSelector((s) => s?.authSlice?.user?.token);

  // ----- UI / local states -----
  const [rowLoading, setRowLoading] = useState(null); // ParameterID | null
  const [cbcLoading, setCbcLoading] = useState(false);
  const [routinePanelLoading, setRoutinePanelLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null); // e.g. "Cancer-Octoscope"
  const [loading, setLoading] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [isCoughOpen, setIsCoughOpen] = useState(false);
  const [coughTargetParamId, setCoughTargetParamId] = useState(null);
  const [cameraTarget, setCameraTarget] = useState(null); // stores detail for camera modal
  const [isStethoscopeOpen, setIsStethoscopeOpen] = useState(false);
  const [stethoscopeTarget, setStethoscopeTarget] = useState(null);

  const isCoughParam = (detail) =>
    detail?.ParameterID === "CO5386" ||
    (detail?.name || "").trim().toLowerCase() === "cough analysis";

  const isCameraParam = (detail) =>
    detail?.ParameterID === "EN4058" ||
    (detail?.name || "").trim().toLowerCase() === "ent";

  const isStethoscopeParam = useCallback((detail) => {
    const pid = (detail?.ParameterID || "").trim().toUpperCase();
    const name = (detail?.name || "").toLowerCase();
    return pid.startsWith("ST") || name.includes("stethoscope");
  }, []);

  // ----- dependent test logic bridge -----
  const {
    results,
    referredBy,
    setReferredBy,
    handleResultChange,
    allResultsFilled,
    saveResults,
    saveReferredBy,
  } = useGeneralTestLogic(tests, initRef);

  // open/close cough modal for a specific row
  const openCoughFor = (paramId) => {
    setCoughTargetParamId(paramId);
    setIsCoughOpen(true);
  };
  const closeCough = () => {
    setIsCoughOpen(false);
    setCoughTargetParamId(null);
  };

  const handleCoughValidated = (payload) => {
    const { status, keepOpen } =
      typeof payload === "object" && payload !== null
        ? { status: payload.status ?? "", keepOpen: !!payload.keepOpen }
        : { status: payload, keepOpen: false };

    if (coughTargetParamId) {
      handleResultChange(coughTargetParamId, typeof status === "string" ? status : "");
    }

    if (!keepOpen) {
      closeCough();
    }
  };

  const openStethoscope = useCallback((detail) => {
    setStethoscopeTarget(detail || null);
    setIsStethoscopeOpen(true);
  }, []);

  const closeStethoscope = useCallback(() => {
    setIsStethoscopeOpen(false);
    setStethoscopeTarget(null);
  }, []);

  const handleStethoscopeResult = useCallback(
    (value) => {
      if (stethoscopeTarget?.ParameterID) {
        const nextValue =
          typeof value === "string" && value.trim() !== ""
            ? value
            : "Completed";
        handleResultChange(stethoscopeTarget.ParameterID, nextValue);
      }
    },
    [stethoscopeTarget, handleResultChange]
  );

  // ----- safe MachineID read -----
  let machineId = undefined;
  try {
    const raw = localStorage.getItem("userDetails");
    const parsed = raw ? JSON.parse(raw) : null;
    machineId = parsed?.MachineID;
  } catch (_) {}

  // ----- utils -----
  const isCbcParam = (pid) => typeof pid === "string" && pid.startsWith("BL");
  const isLastDataNote = (note) =>
    typeof note === "string" && note.toLowerCase().includes("last data");

  const allDetails = useMemo(() => {
    const arr = [];
    for (const grp of tests || []) {
      for (const d of grp?.options || []) arr.push(d);
    }
    return arr;
  }, [tests]);

  const cbcDetails = useMemo(
    () => allDetails.filter((d) => isCbcParam(d?.ParameterID)),
    [allDetails]
  );

  const hasAnyCBC = cbcDetails.length > 0;

  const routinePanelDetails = useMemo(
    () =>
      allDetails.filter((d) =>
        URINE_PANEL_PARAMETER_SET.has(d?.ParameterID)
      ),
    [allDetails]
  );

  const hasRoutinePanel = routinePanelDetails.length > 0;

  const renderUnitRange = (d) =>
    (d?.unit || "-") + (d?.referenceRange ? ` ${d.referenceRange}` : "");
  

  const isCalculated = (parameterId) =>
    Object.prototype.hasOwnProperty.call(CALCULATED_TESTS, parameterId);

  // ----- camera / capture flow -----
  const openModal = (payload) => {
    if (typeof payload === "string") {
      setActiveModal(payload || null);
      setCameraTarget(null);
    } else if (payload) {
      setActiveModal(payload?.name || payload?.ParameterID || null);
      setCameraTarget(payload);
    } else {
      setActiveModal(null);
      setCameraTarget(null);
    }
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setActiveModal(null);
    setCameraTarget(null);
  };

  const ENT_IMAGE_LIMIT = 5;
  const normalizeImageList = useCallback((value) => {
    if (!value) return [];
    if (Array.isArray(value)) {
      return value.filter((img) => typeof img === "string" && img.trim() !== "");
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.filter(
            (img) => typeof img === "string" && img.trim() !== ""
          );
        }
      } catch (_) {
        // ignore JSON parsing issues and fall back to treating it as one capture
      }
      return [trimmed];
    }
    return [];
  }, []);

  const getEntImages = useCallback(
    (parameterId = "EN4058") => normalizeImageList(results?.[parameterId]),
    [normalizeImageList, results]
  );

  const removeEntImage = useCallback(
    (parameterId = "EN4058", index) => {
      if (typeof index !== "number") return;
      const existing = normalizeImageList(results?.[parameterId]);
      if (index < 0 || index >= existing.length) return;
      const updated = existing.filter((_, idx) => idx !== index);
      handleResultChange(parameterId, updated);
      toast.info("ENT image removed.", {
        position: toast.POSITION.TOP_CENTER,
      });
    },
    [handleResultChange, normalizeImageList, results]
  );

  const clearEntImages = useCallback(
    (parameterId = "EN4058") => {
      const existing = normalizeImageList(results?.[parameterId]);
      if (!existing.length) return;
      handleResultChange(parameterId, []);
      toast.info("All ENT images deleted.", {
        position: toast.POSITION.TOP_CENTER,
      });
    },
    [handleResultChange, normalizeImageList, results]
  );

  const handleCapture = async (payload) => {
    const images = Array.isArray(payload)
      ? payload.filter((img) => typeof img === "string" && img.trim() !== "")
      : payload
      ? [payload]
      : [];

    if (images.length === 0) {
      toast.error("Error in capturing image.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    setCapturedImage(images);

    if (!cameraTarget) {
      toast.info("No test selected for this capture.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    // ENT capture: store multiple images (up to ENT_IMAGE_LIMIT)
    if (cameraTarget?.ParameterID === "EN4058") {
      const existing = normalizeImageList(results?.[cameraTarget.ParameterID]);
      const combined = [...existing, ...images].slice(0, ENT_IMAGE_LIMIT);
      handleResultChange(cameraTarget.ParameterID, combined);
      const addedCount = Math.max(0, combined.length - existing.length);
      if (addedCount === 0) {
        toast.info("Maximum ENT image limit reached.", {
          position: toast.POSITION.TOP_CENTER,
        });
      } else {
        toast.success(
          addedCount > 1 ? `${addedCount} ENT images captured.` : "ENT image captured.",
          {
            position: toast.POSITION.TOP_CENTER,
          }
        );
      }
      return;
    }

    const [primaryImage] = images;

    // Cancer Octoscope (existing flow) -> send to backend
    if (cameraTarget?.ParameterID === "CT8716") {
      try {
        setLoading(true);
        await axios.post(
          `${API_BASE_URL}/octoscope/add-octoscope`,
          primaryImage,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Placeholder: pipe results when mapping confirmed
        toast.success("Image processed.", {
          position: toast.POSITION.TOP_CENTER,
        });
      } catch (err) {
        console.error("Capture error:", err?.message || err);
        toast.error("Failed to process captured image.", {
          position: toast.POSITION.TOP_CENTER,
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    // Fallback: store captured image value if another camera-based test appears
    if (cameraTarget?.ParameterID) {
      handleResultChange(cameraTarget.ParameterID, primaryImage);
      toast.success("Image captured.", {
        position: toast.POSITION.TOP_CENTER,
      });
    }
  };

  // ----- CBC bulk fetch -----
  const handleFetchAllCBC = async () => {
    if (!hasAnyCBC) {
      toast.info("No CBC parameters found on this page.");
      return;
    }
    if (!machineId) {
      toast.error(
        "Machine not identified. Please re-login or set up the device.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
      return;
    }

    try {
      setCbcLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/test-one/getCbc?MachineID=${machineId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const note = response?.data?.note;
      const list = response?.data?.getTest?.mydata;
      if (!Array.isArray(list) || list.length === 0) {
        toast.info("No recent CBC data found.");
        return { res: note, code: "CBC", error: "noData" };
      }

      const codeToValue = {};
      for (const item of list) {
        if (item?.test_code != null)
          codeToValue[item.test_code] = item?.result ?? "";
      }

      const pendingUpdates = {};
      let applied = 0;
      for (const d of cbcDetails) {
        const code = (d?.autoFetchKey || "").toString().trim();
        if (!code || code.toUpperCase() === "N/A") continue;
        if (Object.prototype.hasOwnProperty.call(codeToValue, code)) {
          const rawValue = codeToValue[code];
          const value =
            typeof rawValue === "string" ? rawValue : String(rawValue ?? "");
          pendingUpdates[d.ParameterID] = value;
          applied++;
        }
      }
      if (applied > 0) {
        if (isLastDataNote(note)) {
          return {
            res: note,
            code: "CBC",
            error: "lastData",
            updates: pendingUpdates,
            count: applied,
          };
        }

        Object.entries(pendingUpdates).forEach(([parameterId, value]) => {
          handleResultChange(parameterId, value);
        });
        toast.success(`CBC results fetched for ${applied} parameter(s).`, {
          position: toast.POSITION.TOP_CENTER,
        });
        return { res: note, code: "CBC", error: "false", count: applied };
      } else {
        toast.info("CBC data received, but no matching parameters found.");
        return { res: note, code: "CBC", error: "noMatch" };
      }
    } catch (error) {
      console.error("CBC fetch error:", error);
      toast.error("Failed to fetch CBC results. Please try again.", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setCbcLoading(false);
    }
  };

  // ----- per-row fetch for NON-CBC -----
  const handleFetchResult = async (detail) => {
    if (isCbcParam(detail?.ParameterID)) {
      toast.info(
        "CBC parameters are fetched together using the CBC button below."
      );
      return {
        res: "CBC bulk only",
        code: detail?.autoFetchKey || "-",
        error: "cbcBulkOnly",
      };
    }

    const testCode = detail?.autoFetchKey;
    if (!testCode) {
      toast.error("No autoFetchKey found for this test.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return { res: "No Test Code", code: "-", error: "noCode" };
    }
    if (!machineId) {
      toast.error(
        "Machine not identified. Please re-login or set up the device.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
      return { res: "No MachineID", code: testCode, error: "noMachine" };
    }

    try {
      setRowLoading(detail.ParameterID);
      const response = await axios.post(
        `${API_BASE_URL}/test-two/getResults/${machineId}`,
        { test_code: testCode },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const note = response?.data?.note;
      const list = response?.data?.getTest?.mydata;
      if (Array.isArray(list) && list.length > 0) {
        const match = list.find((x) => x?.test_code === testCode) || list[0];
        const value =
          typeof match?.result === "string"
            ? match.result
            : String(match?.result ?? "");

        if (
          typeof note === "string" &&
          note.toLowerCase().includes("last data")
        ) {
          return {
            res: note,
            code: testCode,
            error: "lastData",
            value,
            parameterId: detail?.ParameterID,
          };
        }

        handleResultChange(detail.ParameterID, value);
        toast.success("Result fetched successfully.", {
          position: toast.POSITION.TOP_CENTER,
        });
        return { res: note, code: testCode, error: "false" };
      } else {
        toast.info("No recent data found for this test.", {
          position: toast.POSITION.TOP_CENTER,
        });
        return { res: "No Data", code: testCode, error: "noData" };
      }
    } catch (error) {
      console.error("Per-row fetch error:", error);
      toast.error("Failed to fetch result. Please try again.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return { res: "Request Failed", code: testCode, error: "requestFailed" };
    } finally {
      setRowLoading(null);
    }
  };

  // ----- Routine Panel bulk fetch (urine strip) -----
  const handleFetchRoutinePanel = async () => {
    if (!hasRoutinePanel) {
      toast.info("No Routine Panel parameters found on this page.");
      return;
    }
    if (!machineId) {
      toast.error(
        "Machine not identified. Please re-login or set up the device.",
        {
          position: toast.POSITION.TOP_CENTER,
        }
      );
      return;
    }

    try {
      setRoutinePanelLoading(true);
      const response = await axios.get(
        `${DEFAULT_API_BASE_URL}/test-three/getUrine?MachineID=${machineId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      

      const note = response?.data?.note;
      const tests = response?.data?.getTest;
      const latestEntry = Array.isArray(tests) ? tests[0] : tests;
      const mydata = latestEntry?.mydata;

      if (!mydata || typeof mydata !== "object") {
        toast.info("No recent Routine Panel data found.");
        return { res: note, code: "RoutinePanel", error: "noData" };
      }

      const normalized = Object.entries(mydata).reduce((acc, [key, value]) => {
        if (typeof key === "string") acc[key.toUpperCase()] = value;
        return acc;
      }, {});

      const pendingUpdates = {};
      let applied = 0;
      for (const detail of routinePanelDetails) {
        const code = URINE_PANEL_PARAM_TO_CODE[detail?.ParameterID];
        if (!code) continue;
        if (Object.prototype.hasOwnProperty.call(normalized, code)) {
          const rawValue = normalized[code];
          const value =
            typeof rawValue === "string" ? rawValue : String(rawValue ?? "");
          pendingUpdates[detail.ParameterID] = value;
          applied++;
        }
      }

      if (applied > 0) {
        if (isLastDataNote(note)) {
          return {
            res: note,
            code: "RoutinePanel",
            error: "lastData",
            updates: pendingUpdates,
            count: applied,
          };
        }

        Object.entries(pendingUpdates).forEach(([parameterId, value]) => {
          handleResultChange(parameterId, value);
        });
        toast.success(
          `Routine Panel results fetched for ${applied} parameter(s).`,
          {
            position: toast.POSITION.TOP_CENTER,
          }
        );
        return {
          res: note,
          code: "RoutinePanel",
          error: "false",
          count: applied,
        };
      } else {
        toast.info(
          "Routine Panel data received, but no matching parameters found."
        );
        return { res: note, code: "RoutinePanel", error: "noMatch" };
      }
    } catch (error) {
      console.error("Routine Panel fetch error:", error);
      toast.error("Failed to fetch Routine Panel results. Please try again.", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setRoutinePanelLoading(false);
    }
  };

  return {
    // state
    rowLoading,
    cbcLoading,
    routinePanelLoading,
    isModalOpen,
    activeModal,
    cameraTarget,
    loading,
    capturedImage,
    isStethoscopeOpen,
    handleResultChange,
    hasAnyCBC,
    hasRoutinePanel,
    // cough modal bits
    isCoughOpen,
    openCoughFor,
    closeCough,
    handleCoughValidated,
    isCoughParam,
    isCameraParam,
    isStethoscopeParam,
    openStethoscope,
    closeStethoscope,
    handleStethoscopeResult,
    // data from dependent test logic
    results,
    referredBy,
    setReferredBy,
    allResultsFilled,
    saveResults,
    saveReferredBy,
    // utils
    isCbcParam,
    isCalculated,
    renderUnitRange,
    // actions
    openModal,
    closeModal,
    handleCapture,
    handleFetchAllCBC,
    handleFetchResult,
    handleFetchRoutinePanel,
    getEntImages,
    removeEntImage,
    clearEntImages,
    entImageLimit: ENT_IMAGE_LIMIT,
  };
}
